import axios, { type AxiosRequestConfig, isAxiosError } from 'axios';

import type { KitchenApiClient } from './contracts.js';

export type { KitchenApiClient } from './contracts.js';
export { createDemoApiClient } from './demo-client.js';

type HttpMethod = 'GET' | 'POST' | 'PATCH' | 'DELETE';

export interface HttpTransportRequest {
  readonly baseUrl: string;
  readonly method: HttpMethod;
  readonly path: string;
  readonly body?: unknown;
  readonly headers: Readonly<Record<string, string>>;
}

export interface HttpTransportResponse {
  readonly status: number;
  readonly data: unknown;
}

export type HttpTransport = (
  request: HttpTransportRequest,
) => Promise<HttpTransportResponse>;

export interface HttpKitchenApiClientOptions {
  readonly baseUrl: string;
  readonly tokenProvider?: () => string | undefined | Promise<string | undefined>;
  readonly transport?: HttpTransport;
}

export interface AxiosKitchenApiClientOptions {
  readonly baseUrl: string;
  readonly tokenProvider?: () => string | undefined | Promise<string | undefined>;
  readonly axiosInstance?: AxiosTransport;
}

export interface AxiosTransport {
  readonly request: (
    config: AxiosRequestConfig,
  ) => Promise<{ readonly data: unknown; readonly status?: number }>;
}

const auditLogsPath = '/audit-logs';
const authLoginPath = '/auth/login';
const authLogoutPath = '/auth/logout';
const authMePath = '/auth/me';
const authRefreshPath = '/auth/refresh';
const categoriesPath = '/categories';
const dailyMenusPath = '/daily-menus';
const dashboardPath = '/dashboard';
const inventoryAdjustmentsPath = '/inventory/adjustments';
const inventoryExitsPath = '/inventory/exits';
const inventoryPath = '/inventory';
const kardexPath = '/kardex';
const measurementUnitsPath = '/measurement-units';
const productsPath = '/products';
const usersPath = '/users';

export const OPENAPI_ENDPOINTS = {
  auditLogs: { list: auditLogsPath },
  auth: {
    login: authLoginPath,
    logout: authLogoutPath,
    me: authMePath,
    refresh: authRefreshPath,
  },
  categories: { list: categoriesPath },
  dailyMenus: {
    create: dailyMenusPath,
    list: dailyMenusPath,
    detail: (dailyMenuId: string) => `${dailyMenusPath}/${dailyMenuId}`,
    pdf: (dailyMenuId: string) => `${dailyMenusPath}/${dailyMenuId}/pdf`,
    pdfByDate: `${dailyMenusPath}/pdf`,
  },
  dashboard: dashboardPath,
  inventory: {
    adjustments: inventoryAdjustmentsPath,
    entries: '/inventory/entries',
    exits: inventoryExitsPath,
    list: inventoryPath,
  },
  kardex: {
    list: kardexPath,
    product: (productId: string) => `/kardex/products/${productId}`,
  },
  measurementUnits: { list: measurementUnitsPath },
  products: {
    create: productsPath,
    list: productsPath,
    detail: (productId: string) => `${productsPath}/${productId}`,
    update: (productId: string) => `${productsPath}/${productId}`,
    delete: (productId: string) => `${productsPath}/${productId}`,
    priceHistory: (productId: string) => `${productsPath}/${productId}/price-history`,
  },
  reports: {
    consumption: '/reports/consumption',
    inventory: '/reports/inventory',
    stockAlerts: '/reports/stock-alerts',
  },
  users: {
    list: usersPath,
    create: usersPath,
    update: (userId: string) => `${usersPath}/${userId}`,
    delete: (userId: string) => `${usersPath}/${userId}`,
  },
} as const;

export class KitchenApiClientError extends Error {
  public constructor(
    public readonly statusCode: number,
    public readonly code: string,
    message: string,
    public readonly details: unknown[] = [],
  ) {
    super(message);
  }
}

type RequestFn = <T>(method: HttpMethod, path: string, body?: unknown) => Promise<T>;

function createClientMethods(request: RequestFn): KitchenApiClient {
  return {
    login(body) {
      return request('POST', OPENAPI_ENDPOINTS.auth.login, body);
    },
    logout(body) {
      return request('POST', OPENAPI_ENDPOINTS.auth.logout, body);
    },
    refreshSession(body) {
      return request('POST', OPENAPI_ENDPOINTS.auth.refresh, body);
    },
    me() {
      return request('GET', OPENAPI_ENDPOINTS.auth.me);
    },
    listUsers() {
      return request('GET', OPENAPI_ENDPOINTS.users.list);
    },
    createUser(body) {
      return request('POST', OPENAPI_ENDPOINTS.users.create, body);
    },
    updateUser(userId, body) {
      return request('PATCH', OPENAPI_ENDPOINTS.users.update(userId), body);
    },
    deleteUser(userId) {
      return request('DELETE', OPENAPI_ENDPOINTS.users.delete(userId));
    },
    dashboard() {
      return request('GET', OPENAPI_ENDPOINTS.dashboard);
    },
    listCategories() {
      return request('GET', OPENAPI_ENDPOINTS.categories.list);
    },
    listMeasurementUnits() {
      return request('GET', OPENAPI_ENDPOINTS.measurementUnits.list);
    },
    listProducts() {
      return request('GET', OPENAPI_ENDPOINTS.products.list);
    },
    getProduct(productId) {
      return request('GET', OPENAPI_ENDPOINTS.products.detail(productId));
    },
    createProduct(body) {
      return request('POST', OPENAPI_ENDPOINTS.products.create, body);
    },
    updateProduct(productId, body) {
      return request('PATCH', OPENAPI_ENDPOINTS.products.update(productId), body);
    },
    deleteProduct(productId) {
      return request('DELETE', OPENAPI_ENDPOINTS.products.delete(productId));
    },
    getProductPriceHistory(productId) {
      return request('GET', OPENAPI_ENDPOINTS.products.priceHistory(productId));
    },
    listInventory() {
      return request('GET', OPENAPI_ENDPOINTS.inventory.list);
    },
    listKardex(productId) {
      return request(
        'GET',
        productId === undefined
          ? OPENAPI_ENDPOINTS.kardex.list
          : OPENAPI_ENDPOINTS.kardex.product(productId),
      );
    },
    listAuditLogs() {
      return request('GET', OPENAPI_ENDPOINTS.auditLogs.list);
    },
    recordInventoryExit(body) {
      return request('POST', OPENAPI_ENDPOINTS.inventory.exits, body);
    },
    recordInventoryAdjustment(body) {
      return request('POST', OPENAPI_ENDPOINTS.inventory.adjustments, body);
    },
    registerStockEntry(body) {
      return request('POST', OPENAPI_ENDPOINTS.inventory.entries, body);
    },
    listStockAlerts() {
      return request('GET', OPENAPI_ENDPOINTS.reports.stockAlerts);
    },
    getInventoryReport() {
      return request('GET', OPENAPI_ENDPOINTS.reports.inventory);
    },
    getConsumptionReport() {
      return request('GET', OPENAPI_ENDPOINTS.reports.consumption);
    },
    listDailyMenus(date) {
      const query = date === undefined ? '' : `?date=${encodeURIComponent(date)}`;
      return request('GET', `${OPENAPI_ENDPOINTS.dailyMenus.list}${query}`);
    },
    getDailyMenu(dailyMenuId) {
      return request('GET', OPENAPI_ENDPOINTS.dailyMenus.detail(dailyMenuId));
    },
    registerDailyMenu(body) {
      return request('POST', OPENAPI_ENDPOINTS.dailyMenus.create, body);
    },
    updateDailyMenu(dailyMenuId, body) {
      return request('PATCH', OPENAPI_ENDPOINTS.dailyMenus.detail(dailyMenuId), body);
    },
  };
}

async function fetchTransport(
  request: HttpTransportRequest,
): Promise<HttpTransportResponse> {
  const response = await fetch(`${request.baseUrl}${request.path}`, {
    method: request.method,
    headers: request.headers,
    // Omitir la clave en vez de pasar undefined: con exactOptionalPropertyTypes
    // y los tipos del DOM, `body: undefined` no es asignable a BodyInit | null.
    ...(request.body === undefined ? {} : { body: JSON.stringify(request.body) }),
  });
  const text = await response.text();
  return {
    status: response.status,
    data: text.length === 0 ? undefined : (JSON.parse(text) as unknown),
  };
}

export function createHttpKitchenApiClient(
  options: HttpKitchenApiClientOptions,
): KitchenApiClient {
  const transport = options.transport ?? fetchTransport;

  const request: RequestFn = async <T>(
    method: HttpMethod,
    path: string,
    body?: unknown,
  ): Promise<T> => {
    const token = await options.tokenProvider?.();
    const response = await transport({
      baseUrl: options.baseUrl.replace(/\/$/, ''),
      method,
      path,
      body,
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        ...(token === undefined ? {} : { Authorization: `Bearer ${token}` }),
      },
    });

    if (response.status >= 200 && response.status < 300) {
      return response.data as T;
    }

    throw errorFromResponse(response);
  };

  return createClientMethods(request);
}

export function createAxiosKitchenApiClient(
  options: AxiosKitchenApiClientOptions,
): KitchenApiClient {
  const instance =
    options.axiosInstance ??
    axios.create({
      baseURL: options.baseUrl.replace(/\/$/, ''),
      timeout: 15_000,
      validateStatus: () => true,
    });

  const request: RequestFn = async <T>(
    method: HttpMethod,
    url: string,
    data?: unknown,
  ): Promise<T> => {
    const token = await options.tokenProvider?.();
    const config: AxiosRequestConfig = {
      data,
      method,
      url,
      ...(token === undefined ? {} : { headers: { Authorization: `Bearer ${token}` } }),
    };

    try {
      const response = await instance.request(config);
      const status = response.status ?? 0;

      if (status >= 200 && status < 300) {
        return response.data as T;
      }

      throw errorFromResponse({ status, data: response.data });
    } catch (error) {
      if (error instanceof KitchenApiClientError) {
        throw error;
      }

      if (isAxiosError(error)) {
        const status = error.response?.status ?? 0;
        throw errorFromResponse({
          status,
          data: error.response?.data,
        });
      }

      throw new KitchenApiClientError(
        0,
        'NETWORK_ERROR',
        error instanceof Error ? error.message : 'No se pudo completar la solicitud.',
      );
    }
  };

  return createClientMethods(request);
}

function errorFromResponse(response: HttpTransportResponse): KitchenApiClientError {
  const data = response.data;
  if (isErrorPayload(data)) {
    return new KitchenApiClientError(
      response.status,
      data.code,
      data.message,
      Array.isArray(data.details) ? data.details : [],
    );
  }

  const nestMessage = nestStyleMessage(data);
  if (nestMessage !== undefined) {
    return new KitchenApiClientError(response.status, 'HTTP_ERROR', nestMessage);
  }

  if (response.status === 0) {
    return new KitchenApiClientError(
      0,
      'NETWORK_ERROR',
      'Sin conexión con el servidor. Verifica la red y la URL del API.',
    );
  }

  return new KitchenApiClientError(
    response.status,
    'HTTP_ERROR',
    `Error del servidor (${response.status}).`,
  );
}

function nestStyleMessage(value: unknown): string | undefined {
  if (typeof value !== 'object' || value === null || !('message' in value)) {
    return undefined;
  }

  const message = (value as { message: unknown }).message;
  if (typeof message === 'string' && message.trim().length > 0) {
    return message;
  }

  if (Array.isArray(message)) {
    const parts = message.filter((item): item is string => typeof item === 'string');
    if (parts.length > 0) {
      return parts.join(' ');
    }
  }

  return undefined;
}

function isErrorPayload(value: unknown): value is {
  readonly code: string;
  readonly message: string;
  readonly details?: unknown[];
} {
  return (
    typeof value === 'object' &&
    value !== null &&
    'code' in value &&
    'message' in value &&
    typeof value.code === 'string' &&
    typeof value.message === 'string' &&
    value.message.trim().length > 0
  );
}
